import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';

import { Thumbnail, ThumbnailProps } from '../../app/spaces/CardView/Thumbnail';
import { Messages } from '../../codegen/Localize';
import { BrowserTitleService } from '../../commonInfra/services/BrowserTitleService';
import { HelpService } from '../../commonInfra/services/HelpService';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AdminInsightsGetProjectIdRequest } from '../../ts/ServerApi/requests/AdminInsightsGetProjectIdRequest';
import { IAdminInsightsResult } from '../../ts/ServerApi/types/IAdminInsightsResult';
import { AdminAnalysisTable } from './AdminAnalysisTable';

export interface AdminSiteStatusState {
  thumbnailProps: ThumbnailProps;
  navigateUrl: string;
}

export interface AdminSiteStatusProps {
  siteName: string;
}


export class AdminSiteStatus extends React.PureComponent<AdminSiteStatusProps, AdminSiteStatusState> {
    private projectId: number;
    private isAdminDataSourceEnabled: boolean;
    private learnMoreHref: string;
    private siteUrls: SiteUrls;

    constructor(props: AdminSiteStatusProps) {
        super(props);
        this.state = {
            thumbnailProps: {
                navigateUrl:  null,
                defaultImageUrl: null,
                imageCoverBackground: true,
                name: Messages.adminInsightsProjectName(),
                allowDragAndDrop: false,
                },
            navigateUrl: null
        };
    }

    private server: SessionManagementService = getSessionManagementServiceInstance();

    public componentDidMount() {
        this.setNavigationUrl();
    }

    private hideStatsForLoadTimes  = () => {
        return ServerSettingsUnauthenticated.hideAdminVizStatsForLoadTimes();
      };

    private hidePerformanceofViews  = () => {
        return ServerSettingsUnauthenticated.hideAdminVizPerformanceofViews();
    };

    private isOnlineEnvironment = () => {
        return this.server.isOnlineEnvironment();
    };

    private hidePerformanceofFlowRunsOnSite  = () => {
        return this.server.hideAdminVizPerformanceofFlowRunsOnSite();
    };

    private bridgeEnabled = () => ServerSettingsUnauthenticated.isBridgeEnabled();

    private analysisUrl = (sheetUrlId: string) => {
        return  this.siteUrls?.siteStatusVizUrl(sheetUrlId);
    };

    private adminInsightsCallback = (result: IAdminInsightsResult) => {
        if(this.adminInsightsProjectExists(result)){
            this.projectId = result.projectId;
            this.setState({navigateUrl: this.siteUrls?.projectUrl('' + this.projectId)});
            const thumbnailProperties = {
                navigateUrl:  this.siteUrls?.projectUrl('' + this.projectId),
                defaultImageUrl: 'img/project-thumb-2.png',
                imageCoverBackground: true,
                name: Messages.adminInsightsProjectName(),
                allowDragAndDrop: false,
                };
            this.setState({thumbnailProps: thumbnailProperties});
        } else {
            this.setState({navigateUrl: this.siteUrls?.projectUrl('-1')});
        }
    }

    private setNavigationUrl = () => {
         // Start of Admin Insights code..........
        if(this.server.isAdminDataSourceEnabled()){
            this.isAdminDataSourceEnabled = true;
            this.learnMoreHref = HelpService.getServerHelpUrlByCategory('insights');
            WebHttpClient.sendRequest(new AdminInsightsGetProjectIdRequest()).
            then(result => this.adminInsightsCallback(result));
        }
    }

    private adminInsightsProjectExists = (result: IAdminInsightsResult): boolean => {
        return result.projectId && result.projectId !== -1;
    }

    private renderAdminInsights = (): JSX.Element  => {
        return this.isAdminDataSourceEnabled ?  (
            <>
            <div className="tb-settings-section">
                <div className="tb-settings-group-name">
                    {Messages.adminInsightsHeader()}
                </div>
                <div className="tb-settings-msg">
                    {Messages.adminInsightsMessageOne()}
                    <span className="tb-settings-msg">
                        &nbsp;
                        <a href={this.learnMoreHref} target="_blank" tb-test-id="admin-insights-learn-more-link" rel="noopener noreferrer">
                            {Messages.adminInsightsLearnMore()}
                        </a>
                    </span>
                </div>
                <div className="tb-indent">
                    {this.renderProjectThumbNail()}
                </div>
            </div>
            </>
        ) : null;
    }

    private renderProjectThumbNail = (): JSX.Element => {
        return (this.projectId && this.projectId !== -1) ?
            (<>
                <div className="tb-indent">
                    <div className="tb-settings-subgroup-name">
                        {Messages.adminInsightsShowProjectHeader()}
                    </div>
                    <div className="tb-settings-msg">
                        {Messages.adminInsightsShowProjectMsg()}
                    </div>
                    <div className="tb-thumbnail-admin-insights">
                        <Thumbnail
                            navigateUrl={this.state.thumbnailProps.navigateUrl}
                            defaultImageUrl={this.state.thumbnailProps.defaultImageUrl}
                            imageCoverBackground={this.state.thumbnailProps.imageCoverBackground}
                            name={this.state.thumbnailProps.name}
                            allowDragAndDrop={this.state.thumbnailProps.allowDragAndDrop}
                        />
                    </div>
                </div>
            </>): null;
    }

    private renderAdminAnalysisTable(): JSX.Element {
       return (
       <>
            <div className="tb-settings-section">
                <div className="tb-settings-group-name">
                    {Messages.analysisTableHeaderAnalysis()}
                </div>
                <div className="tb-settings-msg">
                    {Messages.analysisSiteDetails()}
                </div>
                <AdminAnalysisTable
                    hideStatsForLoadTimes={this.hideStatsForLoadTimes()}
                    hidePerformanceofViews={this.hidePerformanceofViews()}
                    hidePerformanceofFlowRunsOnSite={this.hidePerformanceofFlowRunsOnSite()}
                    isOnlineEnvironment={this.isOnlineEnvironment()}
                    isBridgeEnabled={this.bridgeEnabled()}
                    analysisUrl={this.analysisUrl}
                    inServerArea={false}
                    navigateUrl={this.state.navigateUrl}
                />
            </div>
       </>);
    }

    public render(): JSX.Element {
        this.siteUrls = new SiteUrls({
            siteUrlName: this.props.siteName,
            queryString: window.location.search
        });
        BrowserTitleService.setTitleWithTranslatedSpace({translatedSpaceName: Messages.siteStatus()});
        return (
        <>
        <div className="tb-tabs-banner">
            <span className="tb-tabs-banner-title">
                <span className="tb-label">
                    {Messages.siteStatus()}
                </span>
                <span className="tb-count"> &nbsp; </span>
            </span>
        </div>
        <div className="tb-tabs-banner-subplace">
            <div className="tb-scroll tb-scroll-settings">
                <div className="tb-settings-container">
                    {this.renderAdminInsights()}
                    {this.renderAdminAnalysisTable()}
                </div>
            </div>
        </div>
        </>
        );
    }
}